import React, { useEffect, Suspense, lazy } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { ThemeProvider } from '@mui/material/styles';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { TssCacheProvider } from 'tss-react';
import { Route, Routes } from 'react-router-dom';
import { Box } from '@mui/material';
import { intersection } from 'lodash';

// Two emotion caches so tss-react styles reliably override MUI defaults:
// MUI styles are prepended to the top of <head>; tss-react styles are appended
// below them, so component className styles win the cascade.
const muiCache = createCache({ key: 'mui', prepend: true });
const tssCache = createCache({ key: 'tss' });

import { lightTheme } from '../themes';
import { userState } from '../atoms/States';
import { featureFlagsAtom } from '../atoms/FeatureFlagsState';
import { getUser, getFeatureFlags } from '../repositories/HttpRepository';
import Header from '../components/Header';
import TemplateProvider from '../providers/TemplateProvider';
import CreateTemplate from './Template/Create';
import ListDsp from './Dsp/List';
import AddCourier from './Dsp/AddCourier';
import ListDriver from './Dsp/Drivers/ListDriver';
import CreateClient from './Clients/Create';
import EditTelephone from './Clients/EditTelephone';
import ProjectionsContainer from './Projections/index';
import ListTemplate from './Template/List';
import AddWarehouse from './Warehouse/AddWarehouse';
import DetailWarehouse from './Warehouse/DetailWarehouse';
import ListWarehouse from './Warehouse/List/index';
import AddDriver from './Dsp/Drivers/AddDriver';
import ListDspDriver from './Dsp/ListDspDriver';
import UpdateCourier from './Dsp/UpdateCourier';
import JobList from './Job';
import DriverAppealForm from './DriverAppeal/index';
import DriverAppealDetail from './DriverAppeal/detail';
import DriverComplaint from './DriverComplaint';
import DriverComplaintDetail from './DriverComplaint/detail';
import DriverPricing from './DriverPricingSimulation';
import HealthBenefit from './HealthBenefit';
import AssignmentPricing from './DriverPricingSimulation/assignment';
import HealthBenefitPaymentRequest from './HealthBenefitPaymentRequest';
import EmailLog from './EmailLog';
import DriverList from './Driver';
import AssignmentFeedback from './AssignmentFeedback';

import Schedule from './Schedule';
import FormulaList from './Formula';
import FormulaForm from './Formula/form';
import PrefixesManagement from './PrefixesManagement';
import AddPrefixes from './PrefixesManagement/AddPrefixes';
import SftpJobManagement from './SftpJobManagement';
import SftpJobDetail from './SftpJobManagement/detail';
import RedactCustomerInformation from './RedactCustomerInformation';
import DSPUserList from './Dsp/Users/List';
import ProgressIndicator from 'components/ProgressIndicator';
import ForecastManager from './ForecastManager';

const List = lazy(() => import('./Clients/List'));
const Edit = lazy(() => import('./Clients/Edit'));
const EmailApprove = lazy(() => import('./Clients/EmailApprove'));
const UserList = lazy(() => import('./User/UpdatePassword'));
const Routing = lazy(() => import('./Routing'));
const ReportContainer = lazy(() => import('./Report'));
const Sms = lazy(() => import('./Sms'));
const PODThumbDownDetail = lazy(() => import('./PODThumbDownDetail'));
const BillingDispositionTabs = lazy(() => import('./BillingDispositionManagement/BillingDispositionTabs'));
const DetectionRequest = lazy(() => import('./DetectionRequest'));
const RegionFeatures = lazy(() => import('./RegionFeatures'));
const BookingTemplateContainer = lazy(() => import('./BookingTemplates'));
const EditBookingTemplate = lazy(() => import('./BookingTemplates/EditBookingTemplate'));
const LegalManagement = lazy(() => import('./LegalManagement'));
const PickupETAConfiguration = lazy(() => import('./PickupETAConfiguration'));
const PickupETADetail = lazy(() => import('./PickupETAConfiguration/detail/index'));
const ServiceLevelAgreements = lazy(() => import('./ServiceLevelAgreement'));
const SLADetail = lazy(() => import('./ServiceLevelAgreement/SLADetail'));
const DirectoryMapImageReview = lazy(() => import('./DirectoryMapImageReview'));
const ParkingQr = lazy(() => import('./ParkingQr'));

const ADMIN_SCOPES = ['super-admin', 'admin'];
const DETECTION_REQUEST_SCOPES = ['super-admin', 'driver-admin'];

const ROUTERS = [
  { exact: true, path: ['/schedules'], component: Schedule },
  { exact: true, path: ['/maps', '/maps/:id'], component: Routing },
  { exact: false, path: ['/users'], component: UserList },
  // NOTE: v5 used the regex path "/warehouses/:id(\\d+)" so that "/warehouses/add"
  // fell through to ListWarehouse + the AddWarehouse sub. React Router v7 has no
  // inline regex, so "add" is promoted to its own static route (static segments
  // outrank the dynamic ":id"), preserving the original render of list + add.
  {
    exact: true,
    path: '/warehouses/add',
    component: () => (
      <>
        <ListWarehouse />
        <AddWarehouse />
      </>
    ),
  },
  { exact: true, path: '/warehouses/:id', component: DetailWarehouse },
  { exact: false, path: '/warehouses', component: ListWarehouse },
  {
    exact: false,
    path: '/parking-qr',
    scopes: ['super-admin', 'admin', 'warehouse-manager', 'warehouse-staff'],
    component: ParkingQr,
  },
  {
    exact: false,
    path: '/drivers/pricing/simulation',
    component: DriverPricing,
  },
  {
    exact: false,
    path: '/drivers/pricing/assignment',
    component: AssignmentPricing,
  },
  { exact: true, path: '/templates', component: ListTemplate },
  {
    exact: false,
    path: ['/templates/create', '/templates/:version/clone'],
    component: () => (
      <TemplateProvider>
        <CreateTemplate />
      </TemplateProvider>
    ),
  },
  { exact: true, path: '/dsp/drivers', component: ListDspDriver },
  {
    exact: false,
    path: '/dsp',
    component: ListDsp,
    subs: [
      { exact: false, path: '/dsp/users/:courierId', component: DSPUserList },
      { exact: false, path: '/dsp/add', component: AddCourier },
      {
        exact: false,
        path: '/dsp/drivers/:courierId',
        component: ListDriver,
        subs: [
          {
            exact: false,
            path: '/dsp/drivers/:courierId/add',
            component: AddDriver,
          },
        ],
      },
      { exact: false, path: '/dsp/:id', component: UpdateCourier },
    ],
  },
  { exact: false, path: '/report', component: ReportContainer },
  { exact: false, path: '/sms', component: Sms },
  { exact: false, path: '/charts/:name', component: ProjectionsContainer },
  { exact: false, path: '/jobs', component: JobList },
  { exact: true, path: '/sftp-jobs', component: SftpJobManagement },
  { exact: true, path: '/sftp-jobs/:id', component: SftpJobDetail },
  { exact: true, path: '/driver-appeals/:id', component: DriverAppealDetail },
  { exact: false, path: '/driver-appeals', component: DriverAppealForm },
  {
    exact: true,
    path: '/driver-complaints/:id',
    component: DriverComplaintDetail,
  },
  { exact: false, path: '/driver-complaints', component: DriverComplaint },
  {
    exact: true,
    path: '/health-benefit-payment-requests',
    scopes: ['healthcare-manager'],
    component: HealthBenefitPaymentRequest,
  },
  {
    exact: false,
    path: '/health-benefits',
    scopes: ['healthcare-manager'],
    component: HealthBenefit,
  },
  // { exact: false, path: "/drivers", component: DriverList },
  { exact: false, path: '/email-logs', component: EmailLog },
  {
    exact: false,
    path: '/assignment-feedbacks',
    component: AssignmentFeedback,
  },
  {
    exact: false,
    path: '/prefixes-management',
    component: PrefixesManagement,
    subs: [
      {
        exact: false,
        path: ['/prefixes-management/add', '/prefixes-management/:id'],
        component: AddPrefixes,
      },
    ],
  },
  { exact: true, path: '/forecast-management', component: ForecastManager },
  {
    exact: false,
    path: ['/formulas/create', '/formulas/:id'],
    component: FormulaForm,
  },
  { exact: true, path: '/formulas', component: FormulaList },
  {
    exact: true,
    path: '/pod-management',
    component: () => {
      window.location.replace(`${process.env.REACT_APP_EXTERNAL_PORTAL_URL}/pod-management`);
      return null;
    },
  },
  {
    exact: true,
    path: '/pod-thumb-down/:sid/:podId',
    component: PODThumbDownDetail,
  },
  {
    exact: true,
    path: '/detection-request',
    scopes: ['super-admin', 'driver-admin'],
    component: DetectionRequest,
  },
  {
    exact: true,
    path: '/redact-customer-data',
    component: RedactCustomerInformation,
  },
  {
    exact: false,
    path: ['/billing-disposition-dashboard', '/status-board/review-rate', '/status-board/approval-rate', '/status-board/client-lookback', '/status-board'],
    component: BillingDispositionTabs,
  },
  {
    exact: false,
    path: ['/pickup-eta/:region/:weekday/:id', '/pickup-eta/:region/:weekday'],
    component: PickupETADetail,
  },
  { exact: false, path: ['/pickup-eta'], component: PickupETAConfiguration },
  {
    exact: false,
    path: '/client-success-management',
    scopes: ['super-admin', 'ops-manager'],
    component: BillingDispositionTabs,
  },
  {
    exact: true,
    path: ['/regions', '/regions/:region', '/regions/:region/:category', '/regions/:region/:category/:sub'],
    component: RegionFeatures,
  },
  {
    exact: true,
    path: ['/booking-templates/create', '/booking-templates/:id'],
    component: EditBookingTemplate,
    scopes: ['super-admin'],
  },
  {
    exact: false,
    path: '/booking-templates',
    component: BookingTemplateContainer,
    scopes: ['super-admin'],
  },
  {
    exact: false,
    path: ['/legal-management/:tab/:id', '/legal-management/:tab', '/legal-management'],
    component: LegalManagement,
    scopes: ['super-admin', 'legal'],
  },
  {
    exact: true,
    path: '/service-level-agreements/:clientId',
    component: SLADetail,
    scopes: ['super-admin', 'admin'],
  },
  {
    exact: true,
    path: '/service-level-agreements',
    component: ServiceLevelAgreements,
    scopes: ['super-admin', 'admin'],
  },
  {
    exact: true,
    path: '/directory-map-image-review',
    component: DirectoryMapImageReview,
    scopes: ['super-admin', 'admin'],
  },
  {
    exact: false,
    path: ['/', '/clients'],
    component: List,
    subs: [
      { exact: false, path: '/clients/:clientId/settings', component: Edit },
      {
        exact: false,
        path: '/clients/:clientId/email-approve',
        component: EmailApprove,
      },
      { exact: false, path: '/clients/add', component: CreateClient },
      {
        exact: false,
        path: '/clients/:clientId/telephone',
        component: EditTelephone,
      },
    ],
  },
];

function hasScopes(scopes, userScopes) {
  return scopes?.length > 0 ? intersection(scopes, userScopes)?.length > 0 : true;
}

const toArray = (path) => (Array.isArray(path) ? path : [path]);

// Strip the parent `base` prefix from an absolute child path to make it
// relative, as required by React Router v7 descendant <Routes>.
function relativize(childPath, base) {
  if (!base) return childPath;
  if (childPath === base) return '';
  if (childPath.startsWith(`${base}/`)) return childPath.slice(base.length + 1);
  return childPath.replace(/^\//, '');
}

// Convert the v5-style ROUTERS config into v7 <Route> elements.
// `base` is the absolute path of the parent route whose descendant <Routes>
// we are currently rendering ("" at the top level, where paths stay absolute).
function buildRoutes(routes, userScopes, base = '') {
  return routes.flatMap((route) => {
    if (!hasScopes(route?.scopes, userScopes)) return [];

    const Component = route.component;
    const paths = toArray(route.path);
    const hasSubs = Array.isArray(route.subs) && route.subs.length > 0;
    // exact:false (prefix match) or having subs means the route must keep
    // matching deeper URLs, which v7 expresses with a trailing splat.
    const deep = route.exact === false || hasSubs;

    // The base that this route's subs are relative to: the parent path the
    // subs all extend (ignoring a catch-all "/" alias).
    let subBase = '';
    if (hasSubs) {
      const subPaths = route.subs.flatMap((sub) => toArray(sub.path));
      subBase = [...paths].sort((a, b) => b.length - a.length).find((p) => p !== '/' && subPaths.every((sp) => sp.startsWith(p))) || paths.find((p) => p !== '/') || paths[0];
    }

    const element = (
      <>
        <Component />
        {hasSubs && <Routes>{buildRoutes(route.subs, userScopes, subBase)}</Routes>}
      </>
    );

    return paths.map((absPath) => {
      const rel = relativize(absPath, base);
      let path;
      if (base) {
        path = deep ? (rel === '' ? '*' : `${rel}/*`) : rel;
      } else {
        path = deep ? (absPath === '/' ? '*' : `${absPath}/*`) : absPath;
      }
      return <Route key={`${base}|${absPath}`} path={path} element={element} />;
    });
  });
}

function RootContainer() {
  const [user, setUser] = useRecoilState(userState);
  const setFeatureFlags = useSetRecoilState(featureFlagsAtom);

  useEffect(() => {
    getUser().then((resp) => {
      if (resp.ok) {
        setUser({ ...resp.data, scopes: resp.data?.user?.scopes });

        // Fetch feature flags after successful authentication
        getFeatureFlags()
          .then((flagsResp) => {
            if (flagsResp.ok && flagsResp.data) {
              setFeatureFlags(flagsResp.data);
            }
          })
          .catch((err) => {
            // console.error('Failed to fetch feature flags:', err);
            // Feature flags will use defaults if fetch fails
          });
      } else {
        window.location = `${process.env.REACT_APP_OAUTH_URL}/login?next=${encodeURI(window.location.href)}`;
      }
    });
  }, []);

  if (!user) return <ProgressIndicator />;

  return (
    <CacheProvider value={muiCache}>
      <TssCacheProvider value={tssCache}>
        <ThemeProvider theme={lightTheme}>
          <Box>
            <Header user={user} />
            <Suspense fallback={<ProgressIndicator />}>
              <Routes>{buildRoutes(ROUTERS, user?.scopes)}</Routes>
            </Suspense>
          </Box>
        </ThemeProvider>
      </TssCacheProvider>
    </CacheProvider>
  );
}

export default RootContainer;
